"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import {
    ChevronDown,
    ChevronRight,
    Clock,
    Folder,
    FolderOpen,
    Pin,
    Search,
    SquarePen,
} from "lucide-react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { compareSessions, useCommandPrototype } from "@/components/command-team-v2/prototype/store";
import type { ProtoChatSession, ProtoProject } from "@/components/command-team-v2/prototype/types";
import { ChatRowMenu, RenameChatDialog } from "@/components/command-team-v2/chat/chat-actions";
import { ChatStatusDot } from "@/components/command-team-v2/chat/chat-status-dot";
import { NewProjectButton } from "@/components/command-team-v2/chat/command-panel";
import {
    chatDropHandlers,
    OTHER_CHATS_DROP_KEY,
    setChatDragData,
} from "@/components/command-team-v2/chat/chat-dnd";
import { CommandSidebarListSkeleton } from "@/components/command-team-v2/chat/command-v2-loading-skeletons";
import {
    PROJECTS_PAGE_PATH,
    useSidebarLists,
} from "@/components/command-team-v2/chat/sidebar-lists";
import { CommandSidebarScrollArea } from "@/components/command-team-v2/layout/command-sidebar-scroll-area";

interface CommandV2AssistantHistoryProps {
    activeSessionId: string | null;
    onOpenChat: (sessionId: string) => void;
    onNewChatInProject: (projectId: string) => void;
    onClose: () => void;
}

function AssistantChatRow({
    session,
    active,
    projects,
    onSelect,
    onTogglePin,
    onRename,
    onMove,
    onDelete,
    onDragEnd,
}: {
    session: ProtoChatSession;
    active: boolean;
    projects: ProtoProject[];
    onSelect: () => void;
    onTogglePin: () => void;
    onRename: () => void;
    onMove: (projectId: string | null) => void;
    onDelete: () => void;
    onDragEnd?: () => void;
}) {
    return (
        <div
            draggable
            onDragStart={(event) => setChatDragData(event, session.id)}
            onDragEnd={onDragEnd}
            className={cn(
                "group flex min-w-0 items-center gap-0.5 rounded-lg pl-2 pr-1 transition-colors",
                active ? "bg-violet-50 dark:bg-violet-950/30" : "hover:bg-muted/70"
            )}
        >
            <button
                type="button"
                onClick={onSelect}
                className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 text-left"
            >
                <ChatStatusDot session={session} />
                {session.fromSchedule ? (
                    <Clock className="size-3 shrink-0 text-primary/70" />
                ) : null}
                <span
                    className={cn(
                        "min-w-0 flex-1 truncate text-[13px]",
                        active ? "font-medium text-foreground" : "text-foreground/80"
                    )}
                >
                    {session.title}
                </span>
            </button>
            <button
                type="button"
                onClick={onTogglePin}
                title={session.pinned ? "Unpin" : "Pin to top"}
                className={cn(
                    "size-6 shrink-0 items-center justify-center rounded",
                    session.pinned
                        ? "flex text-primary/70 hover:text-primary"
                        : "hidden text-muted-foreground hover:text-foreground group-hover:flex"
                )}
            >
                <Pin className={cn("size-3 rotate-45", session.pinned && "fill-current")} />
            </button>
            <ChatRowMenu
                session={session}
                projects={projects}
                onTogglePin={onTogglePin}
                onRename={onRename}
                onMove={onMove}
                onDelete={onDelete}
            />
        </div>
    );
}

export function CommandV2AssistantHistory({
    activeSessionId,
    onOpenChat,
    onNewChatInProject,
    onClose,
}: CommandV2AssistantHistoryProps) {
    const router = useRouter();
    const {
        state,
        actions,
        loading,
        sessionsLoadMoreRef,
        hasMoreSessions,
        isFetchingMoreSessions,
    } = useCommandPrototype();
    const [historySearch, setHistorySearch] = useState("");
    const [renameChatTarget, setRenameChatTarget] = useState<ProtoChatSession | null>(null);
    const [dragOver, setDragOver] = useState<string | null>(null);

    const lists = useSidebarLists({
        setDragOver,
        onDropChatIntoProject: (sessionId, projectId) => {
            actions.moveChatToProject(sessionId, projectId);
            toast.success("Chat moved");
        },
    });

    const query = historySearch.trim().toLowerCase();
    const matchesSearch = (session: ProtoChatSession) =>
        !query || session.title.toLowerCase().includes(query);
    const chatsForProject = (projectId: string) =>
        state.sessions
            .filter((session) => session.projectId === projectId && matchesSearch(session))
            .sort(compareSessions);
    const otherChats = state.sessions
        .filter((session) => !session.projectId && matchesSearch(session))
        .sort(compareSessions);
    const nothingMatches =
        query.length > 0 &&
        otherChats.length === 0 &&
        state.projects.every((project) => chatsForProject(project.id).length === 0);

    const goToPage = (path: string) => {
        onClose();
        router.push(path);
    };

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="sticky top-0 z-10 shrink-0 bg-background px-3 pb-2 pt-3">
                <Input
                    value={historySearch}
                    onChange={(event) => setHistorySearch(event.target.value)}
                    placeholder="Search chats"
                    startIcon={<Search className="size-3.5 text-muted-foreground" />}
                    className="h-8 text-[13px]"
                />
            </div>
            <CommandSidebarScrollArea className="px-3 pb-3">

            <div className="flex items-center justify-between px-2 pb-1">
                <div className="flex min-w-0 items-center gap-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Projects
                    </p>
                    <button
                        type="button"
                        onClick={() => goToPage(PROJECTS_PAGE_PATH)}
                        className="text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                    >
                        View all
                    </button>
                </div>
                <NewProjectButton
                    onCreate={(name) => {
                        actions.createProject(name);
                        toast.success("Project created", {
                            description: `“${name}” added — move chats into it from their menu.`,
                        });
                    }}
                />
            </div>

            <div {...lists.projects.listProps}>
                {loading?.projects && state.projects.length === 0 ? (
                    <div className="space-y-1 py-1">
                        <CommandSidebarListSkeleton count={3} />
                    </div>
                ) : null}
                {lists.projects.visible.map((project) => {
                    const chats = chatsForProject(project.id);
                    if (query && chats.length === 0) return null;
                    return (
                        <Collapsible key={project.id} defaultOpen className="group/proj mb-1">
                            <div
                                {...lists.projects.rowProps(project.id)}
                                className={cn(
                                    "group/projrow flex w-full cursor-grab items-center gap-0.5 rounded-lg pl-1 pr-1 transition-opacity hover:bg-muted/70 active:cursor-grabbing",
                                    dragOver === project.id &&
                                        "bg-violet-50 ring-1 ring-inset ring-primary/40 dark:bg-violet-950/30",
                                    lists.projects.draggingId === project.id && "opacity-50"
                                )}
                            >
                                <CollapsibleTrigger asChild>
                                    <button
                                        type="button"
                                        aria-label={`Toggle “${project.name}”`}
                                        className="flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground"
                                    >
                                        <ChevronRight className="size-3.5 transition-transform group-data-[state=open]/proj:rotate-90" />
                                    </button>
                                </CollapsibleTrigger>
                                <button
                                    type="button"
                                    onClick={() => {
                                        actions.selectProject(project.id);
                                        goToPage("/workforce/command-team/chat");
                                    }}
                                    className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 text-left"
                                >
                                    <Folder className="size-3.5 shrink-0 text-primary group-data-[state=open]/proj:hidden" />
                                    <FolderOpen className="hidden size-3.5 shrink-0 text-primary group-data-[state=open]/proj:block" />
                                    <span className="min-w-0 flex-1 truncate text-[13px] font-medium">
                                        {project.name}
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onNewChatInProject(project.id)}
                                    aria-label={`New chat in “${project.name}”`}
                                    className="hidden size-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground group-hover/projrow:flex"
                                >
                                    <SquarePen className="size-3.5" />
                                </button>
                            </div>
                            <CollapsibleContent>
                                <div
                                    {...chatDropHandlers({
                                        key: project.id,
                                        setDragOver,
                                        onDropChat: (sessionId) => {
                                            actions.moveChatToProject(sessionId, project.id);
                                            toast.success("Chat moved");
                                        },
                                    })}
                                    className={cn(
                                        "ml-3 space-y-0.5 rounded-lg border-l border-border/60 pl-2 pt-0.5",
                                        dragOver === project.id && "bg-violet-50/60 dark:bg-violet-950/20"
                                    )}
                                >
                                    {loading?.sessions && chats.length === 0 ? (
                                        <CommandSidebarListSkeleton count={3} />
                                    ) : null}
                                    {chats.map((session) => (
                                        <AssistantChatRow
                                            key={session.id}
                                            session={session}
                                            active={session.id === activeSessionId}
                                            projects={state.projects}
                                            onSelect={() => onOpenChat(session.id)}
                                            onTogglePin={() => actions.togglePinChat(session.id)}
                                            onRename={() => setRenameChatTarget(session)}
                                            onMove={(projectId) =>
                                                actions.moveChatToProject(session.id, projectId)
                                            }
                                            onDelete={() => actions.deleteChat(session.id)}
                                            onDragEnd={() => setDragOver(null)}
                                        />
                                    ))}
                                    {!loading?.sessions && chats.length === 0 ? (
                                        <p className="px-2 py-1.5 text-xs text-muted-foreground/60">
                                            No chats yet
                                        </p>
                                    ) : null}
                                </div>
                            </CollapsibleContent>
                        </Collapsible>
                    );
                })}
                {lists.projects.remaining > 0 ? (
                    <button
                        type="button"
                        onClick={lists.projects.loadMore}
                        className="flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-left text-[13px] font-medium text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground"
                    >
                        <ChevronDown className="size-3.5" />
                        Load more
                        <span className="ml-auto text-[11px]">{lists.projects.remaining}</span>
                    </button>
                ) : null}
                {lists.projects.expanded ? (
                    <button
                        type="button"
                        onClick={lists.projects.showLess}
                        className="flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-left text-[13px] font-medium text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground"
                    >
                        <ChevronDown className="size-3.5 rotate-180" />
                        Show less
                    </button>
                ) : null}
            </div>

            <div
                {...chatDropHandlers({
                    key: OTHER_CHATS_DROP_KEY,
                    setDragOver,
                    onDropChat: (sessionId) => {
                        actions.moveChatToProject(sessionId, null);
                        toast.success("Chat moved", { description: "Moved to Other chats." });
                    },
                })}
                className={cn(
                    "rounded-lg",
                    dragOver === OTHER_CHATS_DROP_KEY &&
                        "bg-violet-50/60 ring-1 ring-inset ring-primary/40 dark:bg-violet-950/20"
                )}
            >
            {(!query || otherChats.length > 0 || loading?.sessions) && (
                <p className="px-2 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Other chats
                </p>
            )}
            <div className="space-y-0.5">
                {loading?.sessions && otherChats.length === 0 ? (
                    <CommandSidebarListSkeleton count={6} />
                ) : null}
                {otherChats.map((session) => (
                    <AssistantChatRow
                        key={session.id}
                        session={session}
                        active={session.id === activeSessionId}
                        projects={state.projects}
                        onSelect={() => onOpenChat(session.id)}
                        onTogglePin={() => actions.togglePinChat(session.id)}
                        onRename={() => setRenameChatTarget(session)}
                        onMove={(projectId) => actions.moveChatToProject(session.id, projectId)}
                        onDelete={() => actions.deleteChat(session.id)}
                        onDragEnd={() => setDragOver(null)}
                    />
                ))}
                {hasMoreSessions ? (
                    <div ref={sessionsLoadMoreRef} className="py-2">
                        {isFetchingMoreSessions ? <CommandSidebarListSkeleton count={2} /> : null}
                    </div>
                ) : null}
                {!query && !loading?.sessions && otherChats.length === 0 ? (
                    <p className="px-2 py-1.5 text-xs text-muted-foreground/60">
                        No ungrouped chats
                    </p>
                ) : null}
            </div>
            </div>

            {nothingMatches ? (
                <p className="px-2 py-6 text-center text-xs text-muted-foreground">
                    No chats match “{historySearch.trim()}”.
                </p>
            ) : null}

            <RenameChatDialog
                session={renameChatTarget}
                onClose={() => setRenameChatTarget(null)}
                onRename={(sessionId, title) => {
                    actions.renameChat(sessionId, title);
                    toast.success("Chat renamed", { description: `Now “${title}”.` });
                }}
            />
            </CommandSidebarScrollArea>
        </div>
    );
}
